import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Search, Users, GitMerge, Loader2, Mail, Phone, Home,
  AlertTriangle, CheckCircle, User, Building2, ArrowRight,
  History, ExternalLink
} from "lucide-react";
import { API } from "@/App";

/**
 * ApplicantMerge Component
 * Find and merge duplicate applicant records across Sales and Lettings
 */
export function ApplicantMerge({
  open,
  onOpenChange,
  applicantId,
  applicantName,
  onMergeComplete
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [source, setSource] = useState(null);
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [mergeDirection, setMergeDirection] = useState("keep_current"); // "keep_current" or "keep_selected"

  useEffect(() => {
    if (open && applicantId) {
      findDuplicates();
    }
  }, [open, applicantId]);

  const findDuplicates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/applicants/duplicates/${applicantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuplicates(res.data.duplicates || []);
      setSource(res.data.source);
    } catch (error) {
      console.error("Error finding duplicates:", error);
      toast.error("Failed to search for duplicates");
    } finally {
      setLoading(false);
    }
  };

  const handleMerge = async () => {
    if (!selectedDuplicate) return;

    setMerging(true);
    try {
      const primaryId = mergeDirection === "keep_current" ? applicantId : selectedDuplicate.id;
      const secondaryId = mergeDirection === "keep_current" ? selectedDuplicate.id : applicantId;

      await axios.post(`${API}/applicants/merge`, {
        primary_id: primaryId,
        secondary_id: secondaryId,
        merge_fields: [] // Could add field selection UI later
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Records merged successfully");
      setShowConfirm(false);
      onOpenChange(false);
      
      if (onMergeComplete) {
        onMergeComplete(primaryId);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to merge records");
    } finally {
      setMerging(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "sales": return <Home className="w-4 h-4 text-blue-600" />;
      case "tenant": return <User className="w-4 h-4 text-emerald-600" />;
      case "landlord": return <Building2 className="w-4 h-4 text-purple-600" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case "high":
        return <Badge className="bg-red-100 text-red-700">High Match</Badge>;
      case "medium":
        return <Badge className="bg-amber-100 text-amber-700">Medium Match</Badge>;
      case "low":
        return <Badge className="bg-slate-100 text-slate-700">Possible Match</Badge>;
      default:
        return null;
    }
  };

  const getMatchTypeLabel = (matchType) => {
    switch (matchType) {
      case "email": return "Same email address";
      case "phone": return "Same phone number";
      case "name": return "Similar name";
      default: return "Match found";
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="w-5 h-5 text-blue-600" />
              Find & Merge Duplicates
            </DialogTitle>
            <DialogDescription>
              Search for duplicate records of "{applicantName || 'this applicant'}" across Sales and Lettings.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-muted-foreground">Searching for duplicates...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Source Record */}
              {source && (
                <Card className="border-blue-200 bg-blue-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-blue-700">Current Record</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        {getTypeIcon(source.type)}
                      </div>
                      <div>
                        <p className="font-medium">{source.display_name}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          {source.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {source.email}
                            </span>
                          )}
                          {source.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {source.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge variant="outline" className="ml-auto capitalize">
                        {source.type}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Duplicates Found */}
              {duplicates.length > 0 ? (
                <div className="space-y-3">
                  <h3 className="font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    {duplicates.length} Potential Duplicate{duplicates.length > 1 ? 's' : ''} Found
                  </h3>

                  {duplicates.map((dup) => (
                    <Card 
                      key={dup.id}
                      className={`cursor-pointer transition-all ${
                        selectedDuplicate?.id === dup.id 
                          ? 'border-blue-500 ring-2 ring-blue-200' 
                          : 'hover:border-slate-300'
                      }`}
                      onClick={() => setSelectedDuplicate(dup)}
                      data-testid={`duplicate-${dup.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedDuplicate?.id === dup.id}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {getTypeIcon(dup.type)}
                              <span className="font-medium">{dup.display_name}</span>
                              <Badge variant="outline" className="capitalize text-xs">
                                {dup.role}
                              </Badge>
                              {getConfidenceBadge(dup.confidence)}
                            </div>
                            
                            <div className="text-sm text-muted-foreground space-y-1">
                              {dup.email && (
                                <p className="flex items-center gap-1">
                                  <Mail className="w-3 h-3" />
                                  {dup.email}
                                </p>
                              )}
                              {dup.phone && (
                                <p className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {dup.phone}
                                </p>
                              )}
                              <p className="flex items-center gap-1">
                                <Home className="w-3 h-3" />
                                {dup.linked_record}
                              </p>
                            </div>
                            
                            <p className="text-xs text-blue-600 mt-2">
                              {getMatchTypeLabel(dup.match_type)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="font-medium text-lg">No Duplicates Found</h3>
                  <p className="text-muted-foreground">
                    This applicant appears to be unique in your system.
                  </p>
                </div>
              )}

              {/* Merge Direction Selection */}
              {selectedDuplicate && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <GitMerge className="w-4 h-4" />
                      Choose Primary Record
                    </h4>
                    <p className="text-sm text-muted-foreground mb-3">
                      The primary record will be kept and updated with data from the secondary record.
                    </p>
                    
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 rounded-lg bg-white border cursor-pointer hover:bg-slate-50">
                        <input
                          type="radio"
                          name="mergeDirection"
                          checked={mergeDirection === "keep_current"}
                          onChange={() => setMergeDirection("keep_current")}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <p className="font-medium">Keep current record as primary</p>
                          <p className="text-sm text-muted-foreground">
                            Merge "{selectedDuplicate.display_name}" into "{source?.display_name}"
                          </p>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-3 p-3 rounded-lg bg-white border cursor-pointer hover:bg-slate-50">
                        <input
                          type="radio"
                          name="mergeDirection"
                          checked={mergeDirection === "keep_selected"}
                          onChange={() => setMergeDirection("keep_selected")}
                          className="w-4 h-4"
                        />
                        <div className="flex-1">
                          <p className="font-medium">Keep selected record as primary</p>
                          <p className="text-sm text-muted-foreground">
                            Merge "{source?.display_name}" into "{selectedDuplicate.display_name}"
                          </p>
                        </div>
                      </label>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            {selectedDuplicate && (
              <Button onClick={() => setShowConfirm(true)}>
                <GitMerge className="w-4 h-4 mr-2" />
                Merge Records
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Merge
            </AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-4">
                This will merge the two records together. The secondary record will be archived 
                but not deleted for audit purposes.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg text-sm">
                <p className="mb-2"><strong>Primary (will be kept):</strong></p>
                <p className="mb-3 pl-4">
                  {mergeDirection === "keep_current" ? source?.display_name : selectedDuplicate?.display_name}
                </p>
                <p className="mb-2"><strong>Secondary (will be merged in):</strong></p>
                <p className="pl-4">
                  {mergeDirection === "keep_current" ? selectedDuplicate?.display_name : source?.display_name}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMerge} disabled={merging}>
              {merging ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Merging...
                </>
              ) : (
                <>
                  <GitMerge className="w-4 h-4 mr-2" />
                  Confirm Merge
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * ApplicantHistory Component
 * Shows full history of an applicant across the system
 */
export function ApplicantHistory({
  open,
  onOpenChange,
  applicantId,
  applicantName
}) {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (open && applicantId) {
      fetchHistory();
    }
  }, [open, applicantId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/applicants/history/${applicantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(res.data.history || []);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "sales": return <Home className="w-4 h-4 text-blue-600" />;
      case "tenancy": return <User className="w-4 h-4 text-emerald-600" />;
      case "property_owned": return <Building2 className="w-4 h-4 text-purple-600" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: "bg-green-100 text-green-700",
      completed: "bg-blue-100 text-blue-700",
      fallen_through: "bg-red-100 text-red-700",
      ended: "bg-slate-100 text-slate-700"
    };
    return (
      <Badge className={statusColors[status] || "bg-slate-100 text-slate-700"}>
        {status?.replace("_", " ")}
      </Badge>
    );
  };

  const handleNavigate = (item) => {
    if (item.type === "sales") {
      navigate(`/sales/case/${item.linked_to}`);
    } else if (item.type === "tenancy") {
      navigate(`/admin/lettings/tenant/${item.record_id}`);
    } else if (item.type === "property_owned") {
      navigate(`/admin/lettings/property/${item.linked_to}`);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-blue-600" />
            Applicant History
          </DialogTitle>
          <DialogDescription>
            Full history of "{applicantName || 'this applicant'}" across Sales and Lettings.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : history.length > 0 ? (
          <div className="space-y-3">
            {history.map((item, index) => (
              <Card 
                key={`${item.record_id}-${index}`}
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => handleNavigate(item)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      {getTypeIcon(item.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{item.description}</p>
                        {item.is_related && (
                          <Badge variant="outline" className="text-xs">
                            Related
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {item.role} • {item.date ? new Date(item.date).toLocaleDateString() : "Unknown date"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(item.status)}
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <History className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-medium">No History Found</h3>
            <p className="text-muted-foreground">
              This is the only record for this applicant.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ApplicantMerge;
